import { useEffect, useRef, useState } from "react";
import { Disc3, Pause, Play, X } from "lucide-react";
import { ALL_TRACKS } from "../lib/tracks";

export const AmbientPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [track] = useState(() => ALL_TRACKS[Math.floor(Math.random() * ALL_TRACKS.length)]);
  const [blocked, setBlocked] = useState(true);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = track.src;
    audio.volume = 0.85;

    const tryPlay = () => {
      audio
        .play()
        .then(() => {
          setPlaying(true);
          setBlocked(false);
          cleanup();
        })
        .catch(() => {});
    };

    const cleanup = () => {
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("keydown", tryPlay);
      window.removeEventListener("touchstart", tryPlay);
      window.removeEventListener("wheel", tryPlay);
    };

    if (window.__lenis) window.__lenis.on?.("scroll", tryPlay);
    window.addEventListener("pointerdown", tryPlay);
    window.addEventListener("keydown", tryPlay);
    window.addEventListener("touchstart", tryPlay);
    window.addEventListener("wheel", tryPlay);
    tryPlay();

    const onOtherPlay = (e) => {
      if (e.detail !== "ambient" && !audio.paused) {
        audio.pause();
        setPlaying(false);
      }
    };
    window.addEventListener("cb-audio-play", onOtherPlay);

    return () => {
      cleanup();
      window.removeEventListener("cb-audio-play", onOtherPlay);
      if (window.__lenis) window.__lenis.off?.("scroll", tryPlay);
      audio.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(() => {});
      setPlaying(true);
      window.dispatchEvent(new CustomEvent("cb-audio-play", { detail: "ambient" }));
    }
  };

  return (
    <div
      data-testid="ambient-player"
      className={`fixed bottom-5 left-5 z-[75] transition-all duration-500 ${hidden ? "translate-y-24 opacity-0" : ""}`}
    >
      <div className="flex items-center gap-3 rounded-full border border-gold/30 bg-ink/90 py-2 pl-2.5 pr-4 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
        <button
          data-testid="ambient-toggle"
          onClick={toggle}
          aria-label={playing ? "Pausar música" : "Reproducir música"}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 hover:scale-105"
        >
          {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} className="translate-x-px" fill="currentColor" />}
        </button>
        <div className="pr-2">
          <p className="flex items-center gap-2 text-[12px] font-medium tracking-wide text-cream">
            {playing && <Disc3 size={13} className="animate-[spin_3s_linear_infinite] text-gold" />}
            {track.title}
          </p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-mute">{track.album}</p>
        </div>
        <button
          data-testid="ambient-hide"
          onClick={() => setHidden(true)}
          aria-label="Ocultar reproductor"
          className="p-1 text-mute transition-colors hover:text-gold"
        >
          <X size={14} />
        </button>
      </div>
      <audio ref={audioRef} preload="auto" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} data-testid="ambient-audio" />
      <span className="sr-only">{blocked ? "La música empezará al primer clic" : ""}</span>
    </div>
  );
};
